import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, AlertCircle, Loader2, ShieldCheck, Search, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { API } from "../apiBase";
import { PROFESSIONS } from "../lib/professions";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");
const validPwd = (s) => (s || "").length >= 8 && /[A-Za-z]/.test(s) && /\d/.test(s);

export default function MssanteInscription() {
  const { setSession } = useProAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    last_name: "", first_name: "", email: "", phone: "",
    profession: "",
    password: "", password_confirm: "",
    accept_cgu: false, accept_data: false, website: "",
  });
  const [err, setErr] = useState({});
  const [serverErr, setServerErr] = useState(null); // { msg, showLogin }
  const [submitting, setSubmitting] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [tsState, setTsState] = useState("loading"); // loading | ready | error
  const tsRef = useRef(null);
  const tsId = useRef(null);
  const [profQuery, setProfQuery] = useState("");
  const [profOpen, setProfOpen] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = "cf-turnstile-script";
    const render = () => {
      if (!window.turnstile || !tsRef.current) return;
      try { if (tsId.current) window.turnstile.remove(tsId.current); }
      catch (e) { console.debug("ts remove:", e); }
      try {
        tsId.current = window.turnstile.render(tsRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          callback: (t) => { setTsToken(t); setTsState("ready"); },
          "error-callback": (code) => {
            console.warn("[turnstile] error:", code);
            setTsToken(""); setTsState("error");
          },
          "expired-callback": () => setTsToken(""),
        });
        setTsState("ready");
      } catch (e) {
        console.warn("[turnstile] render failed:", e);
        setTsState("error");
      }
    };
    if (window.turnstile) { render(); return; }
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true; s.defer = true;
      s.onload = render;
      s.onerror = () => { console.warn("[turnstile] script load failed"); setTsState("error"); };
      document.head.appendChild(s);
    } else {
      const i = setInterval(() => { if (window.turnstile) { clearInterval(i); render(); } }, 200);
    }
    // Réseau d'entreprise / extension : si rien après 10 s, proposer un réessai
    const t = setTimeout(() => {
      setTsState((s) => (s === "loading" ? "error" : s));
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  const resetTurnstile = () => {
    setTsToken("");
    setTsState("loading");
    try {
      if (window.turnstile && tsId.current) { window.turnstile.reset(tsId.current); setTsState("ready"); return; }
    } catch (e) { console.debug("ts reset:", e); }
    window.location.reload();
  };

  const profFiltered = profQuery
    ? PROFESSIONS.filter((p) => p.toLowerCase().includes(profQuery.toLowerCase()))
    : PROFESSIONS;

  const computeErrors = (state, token) => {
    const e = {};
    if (state.last_name.trim().length < 2) e.last_name = "Champ requis";
    if (state.first_name.trim().length < 2) e.first_name = "Champ requis";
    if (!validEmail(state.email)) e.email = "Email invalide";
    if (!state.profession) e.profession = "Sélectionnez une profession";
    if (!validPwd(state.password)) e.password = "8 caractères minimum, dont une lettre et un chiffre";
    if (state.password !== state.password_confirm) e.password_confirm = "Les mots de passe ne correspondent pas";
    if (!state.accept_cgu) e.accept_cgu = "Vous devez accepter les CGU";
    if (!state.accept_data) e.accept_data = "Vous devez accepter le traitement des données";
    if (TURNSTILE_SITE_KEY && !token) e.turnstile = "Validez le contrôle anti-robot";
    return e;
  };

  // Validation réactive : dès la 1re soumission, les erreurs se mettent à jour
  // en direct quand l'utilisateur corrige un champ, coche une case ou valide le Turnstile.
  useEffect(() => {
    if (submittedOnce) setErr(computeErrors(f, tsToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, tsToken, submittedOnce]);

  const FIELD_LABELS = {
    last_name: "Nom", first_name: "Prénom", email: "Email", phone: "Téléphone",
    profession: "Profession", password: "Mot de passe", password_confirm: "Confirmation du mot de passe",
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerErr(null);
    setSubmittedOnce(true);
    const e = computeErrors(f, tsToken);
    setErr(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pro/register`, { ...f, turnstile_token: tsToken });
      setSession(data.token, data.user);
      navigate("/mssante/bienvenue?welcome=1");
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 409) {
        setServerErr({ msg: "Un compte existe déjà avec cet email. Connectez-vous ou réinitialisez votre mot de passe.", showLogin: true });
      } else if (typeof detail === "string" && detail.toLowerCase().includes("anti-robot")) {
        console.warn("[turnstile] rejet serveur — reset du widget");
        setServerErr({ msg: "Le contrôle anti-robot n'a pas abouti. Validez à nouveau la case ci-dessus puis réessayez." });
        resetTurnstile();
      } else if (status === 422 && Array.isArray(detail)) {
        const first = detail[0] || {};
        const fieldKey = (first.loc || []).slice(-1)[0];
        const label = FIELD_LABELS[fieldKey] || fieldKey || "un champ";
        setServerErr({ msg: `Le champ « ${label} » est invalide : ${first.msg || "vérifiez sa valeur."}` });
      } else if (status === 429) {
        setServerErr({ msg: "Trop de tentatives. Patientez une minute puis réessayez." });
      } else if (typeof detail === "string") {
        setServerErr({ msg: detail });
      } else {
        setServerErr({ msg: "Une erreur technique est survenue. Réessayez dans quelques instants — si le problème persiste, contactez-nous." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-inscription">
      <SEO title="Créer mon espace sécurisé · MetaConnect MSSanté"
           description="Créez votre espace professionnel sécurisé en moins de 2 minutes : boîte MSSanté conforme ANS, hébergement HDS, accès par Pro Santé Connect."
           canonical="/mssante/inscription" />
      <section className="hex-grid pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="max-w-xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-3">
              <ShieldCheck size={13} /> Espace sécurisé
            </div>
            <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold">
              Créer mon espace sécurisé
            </h1>
            <p className="mt-3 text-[14px] text-[var(--mc-muted)]">
              Renseignez vos informations professionnelles. Vos données sont protégées et chiffrées.
            </p>
          </div>

          <form onSubmit={submit} className="mc-card p-6 lg:p-8 grid gap-4" noValidate>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nom *" testid="ins-last" value={f.last_name} onChange={(v) => set("last_name", v)} error={err.last_name} />
              <Field label="Prénom *" testid="ins-first" value={f.first_name} onChange={(v) => set("first_name", v)} error={err.first_name} />
            </div>
            <Field label="Email professionnel *" type="email" testid="ins-email" value={f.email} onChange={(v) => set("email", v)} error={err.email} placeholder="prenom.nom@exemple.fr" />
            <Field label="Téléphone (optionnel)" testid="ins-phone" value={f.phone} onChange={(v) => set("phone", v)} />

            <div className="relative">
              <Lbl>Profession *</Lbl>
              <div className="relative">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
                <input
                  data-testid="ins-profession"
                  className="mc-input w-full pl-11 pr-10 cursor-pointer"
                  value={profQuery || f.profession}
                  onChange={(e) => { setProfQuery(e.target.value); set("profession", ""); setProfOpen(true); }}
                  onFocus={() => setProfOpen(true)}
                  onClick={() => setProfOpen(true)}
                  onBlur={() => setTimeout(() => setProfOpen(false), 150)}
                  placeholder="Cliquez pour choisir ou saisissez pour rechercher..."
                  autoComplete="off"
                />
                <ChevronDown size={16}
                             onMouseDown={(e) => { e.preventDefault(); setProfOpen((o) => !o); }}
                             className={`absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--mc-muted)] cursor-pointer transition-transform ${profOpen ? "rotate-180" : ""}`} />
              </div>
              {profOpen && profFiltered.length > 0 && (
                <ul className="absolute z-20 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-[rgba(127,179,255,0.22)] bg-[var(--mc-navy-900)] shadow-lg" data-testid="ins-profession-list">
                  {profFiltered.slice(0, 60).map((p) => (
                    <li key={p}
                        onMouseDown={(e) => { e.preventDefault(); set("profession", p); setProfQuery(p); setProfOpen(false); }}
                        className="px-4 py-2 text-[13px] text-[#334155] hover:bg-[rgba(45,123,255,0.16)] cursor-pointer">
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              {err.profession && <Err>{err.profession}</Err>}
            </div>

            <Field label="Mot de passe *" type="password" testid="ins-pwd"
                   value={f.password} onChange={(v) => set("password", v)} error={err.password}
                   helper="8 caractères minimum, dont une lettre et un chiffre" />
            <Field label="Confirmer le mot de passe *" type="password" testid="ins-pwd2"
                   value={f.password_confirm} onChange={(v) => set("password_confirm", v)}
                   error={err.password_confirm} />

            <Check2 testid="ins-cgu" checked={f.accept_cgu} onChange={(v) => set("accept_cgu", v)} error={err.accept_cgu}>
              J'accepte les <Link to="/cgu" target="_blank" className="text-[#0284C7] underline">CGU</Link> et les <Link to="/cgv" target="_blank" className="text-[#0284C7] underline">CGV</Link> de MetaConnect.
            </Check2>
            <Check2 testid="ins-data" checked={f.accept_data} onChange={(v) => set("accept_data", v)} error={err.accept_data}>
              Je consens au traitement de mes données conformément à la <Link to="/donnees" target="_blank" className="text-[#0284C7] underline">charte RGPD</Link>.
            </Check2>

            {/* Honeypot */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="pt-1">
                <div ref={tsRef} />
                {tsState === "error" && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-800" data-testid="ins-turnstile-error">
                    <AlertCircle size={15} className="mt-0.5 shrink-0" />
                    <span>
                      Le contrôle anti-robot n'a pas pu se charger (réseau d'entreprise ou extension de confidentialité&nbsp;?).{" "}
                      <button type="button" onClick={resetTurnstile} className="underline font-semibold" data-testid="ins-turnstile-retry">
                        Réessayer
                      </button>
                    </span>
                  </div>
                )}
                {err.turnstile && tsState !== "error" && <Err>{err.turnstile}</Err>}
              </div>
            )}

            {serverErr && (
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700" data-testid="ins-server-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>
                  {serverErr.msg}
                  {serverErr.showLogin && (
                    <> <Link to="/mssante/connexion" className="underline font-semibold" data-testid="ins-goto-login">Se connecter</Link></>
                  )}
                </span>
              </div>
            )}

            <button type="submit" disabled={submitting} data-testid="ins-submit"
                    className="btn-primary w-full px-6 py-3 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? "Création de votre espace..." : "Créer mon espace sécurisé"}
              {!submitting && <ArrowRight size={14} />}
            </button>

            <p className="text-center text-[12px] text-[var(--mc-muted)]">
              Déjà un compte&nbsp;? <Link to="/mssante/connexion" className="text-[#0284C7] link-underline">Se connecter</Link>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Lbl({ children }) { return <span className="block text-[11px] uppercase tracking-[0.16em] text-[#475569] mb-2">{children}</span>; }
function Err({ children }) { return <p className="mt-1.5 text-[12px] text-red-600 inline-flex items-center gap-1"><AlertCircle size={12} /> {children}</p>; }
function Field({ label, value, onChange, error, type = "text", placeholder, inputMode, testid, helper }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input type={type} inputMode={inputMode} data-testid={testid}
             className="mc-input w-full" value={value || ""}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {helper && !error && <p className="mt-1.5 text-[11px] text-[var(--mc-muted)]">{helper}</p>}
      {error && <Err>{error}</Err>}
    </div>
  );
}
function Check2({ checked, onChange, children, testid, error }) {
  return (
    <div>
      <label className="flex items-start gap-3 text-[12.5px] text-[var(--mc-muted)] cursor-pointer">
        <input type="checkbox" data-testid={testid} className="mt-1 accent-[var(--mc-blue-500)]"
               checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{children}</span>
      </label>
      {error && <Err>{error}</Err>}
    </div>
  );
}
